import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  CARE_POLICIES, CATEGORIES, LAUNDRY_GROUPS, LAYERS,
} from '@wardrobe/shared';
import { api, type ItemDetail as ItemDaten } from '../api.js';
import { Abschnitt, Button, Card, EINGABE, ErrorBox, Feld, ItemPicker, Loading, Screen, StateBadge, Toggle, categoryLabel, formatCents, carePolicyLabel, itemActionLabel, laundryGroupLabel, layerLabel, learnedFieldLabel, learnedValue} from '../ui.js';

/** Welche Aktionen aus welchem Zustand sinnvoll sind — spiegelt SCHEMA.md §13. */
const ACTIONS: Record<string, { action: string; label: string }[]> = {
  clean: [
    { action: 'wear', label: 'Heute getragen' },
    { action: 'to-hamper', label: 'In den Korb' },
  ],
  worn_ok: [
    { action: 'wear', label: 'Heute getragen' },
    { action: 'to-hamper', label: 'In den Korb' },
  ],
  soiled: [{ action: 'to-hamper', label: 'In den Korb' }],
  drying: [{ action: 'drying-done', label: 'Ist trocken' }],
  ironing: [{ action: 'ironed', label: 'Gebügelt' }],
  at_care: [{ action: 'care-returned', label: 'Zurück aus der Reinigung' }],
};

export function ItemDetail({ id, navigate }: {
  id: string;
  navigate: (to: string) => void;
}): ReactNode {
  const qc = useQueryClient();
  const item = useQuery({ queryKey: ['item', id], queryFn: () => api.item(id) });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['item', id] });
    void qc.invalidateQueries({ queryKey: ['items'] });
    void qc.invalidateQueries({ queryKey: ['hamper'] });
    void qc.invalidateQueries({ queryKey: ['recommend'] });
  };

  const act = useMutation({
    mutationFn: (action: string) => api.action(id, action),
    onSuccess: invalidate,
  });
  const resetLearning = useMutation({
    mutationFn: () => api.resetLearning(id),
    onSuccess: invalidate,
  });
  const vorschlagbar = useMutation({
    mutationFn: (value: boolean) => api.updateItem(id, { suggestable: value }),
    onSuccess: invalidate,
  });
  const [bearbeiten, setBearbeiten] = useState(false);

  if (item.isLoading) return <Loading />;
  if (item.error !== null) {
    return (
      <Screen title="Teil">
        <ErrorBox error={item.error} />
      </Screen>
    );
  }
  const data = item.data;
  if (data === undefined) return null;

  const cpw =
    data.purchasePriceCents !== null && data.totalWears > 0
      ? Math.round(data.purchasePriceCents / data.totalWears)
      : null;

  return (
    <Screen
      title={data.name}
      action={<Button variant="ghost" onClick={() => navigate('/schrank')}>Zurück</Button>}
    >
      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <StateBadge state={data.lifecycleState} />
          <span className="text-xs text-white/55">
            {data.totalWears}× getragen · {formatCents(cpw)}/Tragen
          </span>
        </div>

        {!data.availability.available && (
          <p className="mt-3 rounded-lg bg-[#ff9f0a]/12 p-2 text-xs text-[#ffc266]">
            Nicht verfügbar: {data.availability.reasons.join(', ')}
          </p>
        )}
        {data.needsCleaning && (
          <p className="mt-2 rounded-lg bg-purple-500/10 p-2 text-xs text-purple-200">
            Reinigung fällig ({data.wearsSinceClean} von {data.wearsPerClean} Tragungen) — das
            blockiert nichts, ist aber ein Hinweis.
          </p>
        )}

        {bearbeiten ? (
          <Stammdaten item={data} onFertig={() => setBearbeiten(false)} />
        ) : (
          <>
            <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
              <Row label="Kategorie" value={categoryLabel(data.category)} />
              <Row label="Schicht" value={layerLabel(data.layer)} />
              <Row label="Wärme" value={`${data.warmthPoints}/10`} />
              <Row label="Formalität" value={`${data.formality}/5`} />
              <Row label="Pflege" value={carePolicyLabel(data.carePolicy)} />
              <Row label="Wäschegruppe" value={laundryGroupLabel(data.laundryGroup)} />
              <Row label="Seit Wäsche" value={`${data.wearsSinceWash}/${data.wearsPerWash}`} />
              <Row label="Bügeln" value={data.needsIroning ? 'ja' : 'nein'} />
            </dl>
            <Button variant="ghost" className="mt-3 w-full" onClick={() => setBearbeiten(true)}>
              Stammdaten bearbeiten
            </Button>
          </>
        )}

        {/* D-35: Socken, Unterwäsche und Sportsachen werden getragen und gewaschen, aber
            nicht entschieden. Ohne diesen Schalter traten sie über ihre Schicht als
            Oberteil gegen Hemden an. */}
        <div className="border-t border-white/8 pt-1">
          <Toggle
            label="Für Vorschläge berücksichtigen"
            hint="Aus: Das Teil bleibt im Schrank, wird gewaschen und gezählt — es steht nur nie zur Wahl."
            value={data.suggestable}
            disabled={vorschlagbar.isPending}
            onChange={(v) => vorschlagbar.mutate(v)}
          />
          <ErrorBox error={vorschlagbar.error} />
        </div>
      </Card>

      <div className="mb-4 flex flex-wrap gap-2">
        {(ACTIONS[data.lifecycleState] ?? []).map((a) => (
          <Button key={a.action} onClick={() => act.mutate(a.action)} disabled={act.isPending}>
            {a.label}
          </Button>
        ))}
        {data.history.length > 0 && (
          <Button variant="ghost" onClick={() => act.mutate('undo')} disabled={act.isPending}>
            Letzte Aktion rückgängig
          </Button>
        )}
      </div>
      <ErrorBox error={act.error} />
      {act.data?.alreadyLoggedToday === true && (
        <p className="mb-4 rounded-xl bg-slate-700/40 p-3 text-sm text-white/75">
          Für heute war das Teil bereits eingetragen — die Zähler bleiben unverändert.
        </p>
      )}

      {/* §7: „Was hat die App über dieses Teil gelernt?" plus Reset. */}
      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Was die App gelernt hat</p>
          {data.learning.length > 0 && (
            <Button variant="ghost" onClick={() => resetLearning.mutate()}>
              Zurücksetzen
            </Button>
          )}
        </div>
        {data.learning.length === 0 ? (
          <p className="mt-2 text-xs text-white/45">
            Noch nichts. Für die Wärme braucht es Rückmeldungen und das nachträglich
            bestätigte Wetter des Tages; für die Pflegewerte ein paar Durchgänge durch
            Wäsche und Reinigung — die App liest sie aus deinem Verhalten ab, statt zu
            fragen.
          </p>
        ) : (
          <ul className="mt-3 space-y-2 text-xs text-white/55">
            {data.learning.slice(0, 8).map((e) => (
              <li key={String(e['id'])} className="border-l-2 border-sky-500/40 pl-3">
                <span className="text-white/75">
                  {learnedFieldLabel(String(e['field']))}:{' '}
                  {learnedValue(String(e['field']), Number(e['old_value']))} →{' '}
                  {learnedValue(String(e['field']), Number(e['new_value']))}
                </span>
                <br />
                {String(e['reason'])}
              </li>
            ))}
          </ul>
        )}
        <ErrorBox error={resetLearning.error} />
      </Card>

      <Card>
        <p className="text-sm font-medium">Verlauf</p>
        <ul className="mt-3 space-y-1 text-xs text-white/55">
          {data.history.slice(0, 12).map((h) => (
            <li key={String(h['id'])} className="flex justify-between gap-2">
              <span>
                {itemActionLabel(String(h['action']))}
                {h['undone_at'] !== null && ' (zurückgenommen)'}
              </span>
              <span className="shrink-0 text-white/30">{String(h['local_date'])}</span>
            </li>
          ))}
        </ul>
      </Card>
      <div className="mt-4">
        <CompatCard itemId={id} itemName={data.name} />
      </div>
    </Screen>
  );
}

/**
 * D-40: Stammdaten am einzelnen Teil ändern.
 *
 * Dieser Bildschirm zeigte Wärme, Formalität, Schicht und die Pflegewerte als Text an —
 * ändern liess sich davon nach dem Anlegen **nichts**, außer von Hand per PATCH. Das ist
 * ausgerechnet der häufigste Fall: „dieser Mantel ist wärmer, als die App denkt."
 *
 * Geschickt wird nur, was angefasst wurde. Ein unberührtes Feld taucht im Rumpf gar nicht
 * auf und kann deshalb auch nichts überschreiben — wichtig, weil die Pflege-Lernschleife
 * (D-38) an denselben Spalten arbeitet.
 */
function Stammdaten({ item, onFertig }: { item: ItemDaten; onFertig: () => void }): ReactNode {
  const qc = useQueryClient();
  const [patch, setPatch] = useState<Record<string, unknown>>({});
  const setzen = (feld: string, wert: unknown): void => setPatch((p) => ({ ...p, [feld]: wert }));

  // Nur Text und Zahl werden hier je angezeigt; alles andere wäre ein Programmfehler und
  // soll dann leer bleiben statt als „[object Object]" im Eingabefeld zu stehen.
  const text = (feld: keyof ItemDaten): string => {
    const wert = feldWert(patch, item, feld);
    if (typeof wert === 'string') return wert;
    return typeof wert === 'number' ? String(wert) : '';
  };
  const zahl = (feld: keyof ItemDaten): number => {
    const wert = feldWert(patch, item, feld);
    return typeof wert === 'number' ? wert : 0;
  };
  const jaNein = (feld: keyof ItemDaten): boolean => feldWert(patch, item, feld) === true;

  /** Leere Eingabe heisst „keine Angabe", nicht „leerer Text". */
  const setzenText = (feld: string, roh: string, nullbar = true): void =>
    setzen(feld, roh.trim() === '' && nullbar ? null : roh);
  const setzenZahl = (feld: string, roh: string, min: number, max: number): void => {
    const n = Number.parseFloat(roh.replace(',', '.'));
    setzen(feld, Number.isNaN(n) ? min : Math.min(max, Math.max(min, n)));
  };

  const speichern = useMutation({
    mutationFn: () => api.updateItem(item.id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['item', item.id] });
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['recommend'] });
      void qc.invalidateQueries({ queryKey: ['defaults'] });
      onFertig();
    },
  });

  const reinigung = feldWert(patch, item, 'wearsPerClean') as number | null;

  return (
    <div className="mt-4">
      <Feld label="Name">
        <input value={text('name')} onChange={(e) => setzenText('name', e.target.value, false)} className={EINGABE} />
      </Feld>

      <div className="grid grid-cols-2 gap-3">
        <Feld label="Kategorie">
          <select value={text('category')} onChange={(e) => setzen('category', e.target.value)} className={EINGABE}>
            {KATEGORIEN.map((c) => (
              <option key={c} value={c}>{categoryLabel(c)}</option>
            ))}
          </select>
        </Feld>
        <Feld label="Schicht" hinweis="Bestimmt, an welcher Stelle das Teil vorgeschlagen wird.">
          <select value={text('layer')} onChange={(e) => setzen('layer', e.target.value)} className={EINGABE}>
            {LAYERS.map((l) => (
              <option key={l} value={l}>{layerLabel(l)}</option>
            ))}
          </select>
        </Feld>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Feld label="Marke">
          <input value={text('brand')} onChange={(e) => setzenText('brand', e.target.value)} className={EINGABE} />
        </Feld>
        <Feld label="Größe">
          <input value={text('size')} onChange={(e) => setzenText('size', e.target.value)} className={EINGABE} />
        </Feld>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Feld label="Farbe">
          <input value={text('colorPrimaryName')} onChange={(e) => setzenText('colorPrimaryName', e.target.value)} className={EINGABE} />
        </Feld>
        <Feld label="Muster">
          <input value={text('pattern')} onChange={(e) => setzenText('pattern', e.target.value)} className={EINGABE} />
        </Feld>
      </div>

      <Abschnitt titel="Wärme und Eignung" />
      <div className="grid grid-cols-2 gap-3">
        <Feld label="Wärme (0–10)" hinweis="Der Startwert. Gelernte Korrekturen bleiben davon unberührt.">
          <input value={zahl('warmthPoints')} inputMode="numeric" onChange={(e) => setzenZahl('warmthPoints', e.target.value, 0, 10)} className={`tnum ${EINGABE}`} />
        </Feld>
        <Feld label="Formalität (1–5)">
          <input value={zahl('formality')} inputMode="numeric" onChange={(e) => setzenZahl('formality', e.target.value, 1, 5)} className={`tnum ${EINGABE}`} />
        </Feld>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Feld label="Regen (0–3)" hinweis="Ab 2 gilt das Teil als regentauglich.">
          <input value={zahl('rainResistance')} inputMode="numeric" onChange={(e) => setzenZahl('rainResistance', e.target.value, 0, 3)} className={`tnum ${EINGABE}`} />
        </Feld>
        <Feld label="Wind (0–3)">
          <input value={zahl('windResistance')} inputMode="numeric" onChange={(e) => setzenZahl('windResistance', e.target.value, 0, 3)} className={`tnum ${EINGABE}`} />
        </Feld>
      </div>

      <Abschnitt titel="Pflege" />
      <div className="grid grid-cols-2 gap-3">
        <Feld label="Pflege">
          <select value={text('carePolicy')} onChange={(e) => setzen('carePolicy', e.target.value)} className={EINGABE}>
            {CARE_POLICIES.map((p) => (
              <option key={p} value={p}>{carePolicyLabel(p)}</option>
            ))}
          </select>
        </Feld>
        <Feld label="Wäschegruppe">
          <select value={text('laundryGroup')} onChange={(e) => setzen('laundryGroup', e.target.value)} className={EINGABE}>
            {LAUNDRY_GROUPS.map((g) => (
              <option key={g} value={g}>{laundryGroupLabel(g)}</option>
            ))}
          </select>
        </Feld>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Feld label="Waschtemperatur (°C)" hinweis="Leer lassen, wenn kein Etikett vorliegt.">
          <input
            value={text('washTempC')}
            inputMode="numeric"
            placeholder="—"
            onChange={(e) =>
              setzen('washTempC', e.target.value.trim() === '' ? null : Math.min(95, Math.max(0, Number.parseInt(e.target.value, 10) || 0)))
            }
            className={`tnum ${EINGABE}`}
          />
        </Feld>
        <Feld label="Wäsche nach Tragungen">
          <input value={zahl('wearsPerWash')} inputMode="numeric" onChange={(e) => setzenZahl('wearsPerWash', e.target.value, 1, 100)} className={`tnum ${EINGABE}`} />
        </Feld>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Feld label="Trockenzeit (h)">
          <input value={zahl('dryingHours')} inputMode="decimal" onChange={(e) => setzenZahl('dryingHours', e.target.value, 0, 200)} className={`tnum ${EINGABE}`} />
        </Feld>
        <Feld label="Ruhezeit (h)" hinweis="So lange gilt das Teil nach dem Tragen als nicht verfügbar.">
          <input value={zahl('restHours')} inputMode="decimal" onChange={(e) => setzenZahl('restHours', e.target.value, 0, 500)} className={`tnum ${EINGABE}`} />
        </Feld>
      </div>

      <p className="mt-3 text-xs text-white/55">Reinigung</p>
      <div className="mt-1 flex gap-2">
        <button
          onClick={() => setzen('wearsPerClean', null)}
          className={`flex-1 rounded-full px-3 py-2 text-sm ${reinigung === null ? 'bg-white text-black' : 'bg-white/8 text-white/75'}`}
        >
          Nur bei Bedarf
        </button>
        <button
          onClick={() => setzen('wearsPerClean', reinigung ?? 15)}
          className={`flex-1 rounded-full px-3 py-2 text-sm ${reinigung !== null ? 'bg-white text-black' : 'bg-white/8 text-white/75'}`}
        >
          Nach Tragungen
        </button>
      </div>
      {reinigung !== null && (
        <input
          value={reinigung}
          inputMode="numeric"
          onChange={(e) => setzenZahl('wearsPerClean', e.target.value, 1, 200)}
          className={`tnum ${EINGABE}`}
        />
      )}

      <Toggle
        label="Bügeln"
        hint={'Nach dem Trocknen wartet das Teil dann auf „gebügelt", bevor es wieder zur Wahl steht.'}
        value={jaNein('needsIroning')}
        onChange={(v) => setzen('needsIroning', v)}
      />

      <Abschnitt titel="Kauf" />
      <div className="grid grid-cols-2 gap-3">
        <Feld label="Kaufdatum">
          <input type="date" value={text('purchaseDate')} onChange={(e) => setzenText('purchaseDate', e.target.value)} className={EINGABE} />
        </Feld>
        <Feld label="Preis (€)">
          <input
            value={
              feldWert(patch, item, 'purchasePriceCents') === null
                ? ''
                : String((feldWert(patch, item, 'purchasePriceCents') as number) / 100).replace('.', ',')
            }
            inputMode="decimal"
            placeholder="—"
            onChange={(e) =>
              setzen(
                'purchasePriceCents',
                e.target.value.trim() === ''
                  ? null
                  : Math.max(0, Math.round(Number(e.target.value.replace(',', '.')) * 100)),
              )
            }
            className={`tnum ${EINGABE}`}
          />
        </Feld>
      </div>

      <Feld label="Notiz">
        <textarea value={text('notes')} rows={2} onChange={(e) => setzenText('notes', e.target.value)} className={EINGABE} />
      </Feld>

      <div className="mt-4 flex gap-2">
        <Button
          className="flex-1"
          disabled={speichern.isPending || Object.keys(patch).length === 0}
          onClick={() => speichern.mutate()}
        >
          {Object.keys(patch).length === 0 ? 'Nichts geändert' : 'Speichern'}
        </Button>
        <Button variant="ghost" onClick={onFertig}>Abbrechen</Button>
      </div>
      <ErrorBox error={speichern.error} />
    </div>
  );
}

/** Aktueller Wert eines Feldes: was im Entwurf steht, sonst der gespeicherte Stand. */
function feldWert(
  patch: Record<string, unknown>,
  item: ItemDaten,
  feld: keyof ItemDaten,
): unknown {
  return feld in patch ? patch[feld] : item[feld];
}

/** Nach der deutschen Beschriftung sortiert — so sucht man in einer Auswahlliste. */
const KATEGORIEN = [...CATEGORIES].sort((a, b) =>
  categoryLabel(a).localeCompare(categoryLabel(b), 'de'),
);

/**
 * D-31: „Dieses Sakko passt nur zu diesen Hemden."
 *
 * Zwei Listen, mehr braucht es nicht: eine Positivliste je Slot und ein Ausschluss.
 * Was hier gepflegt wird, wirkt sofort auf die Vorschläge — die Engine erzeugt
 * unmögliche Kombinationen dann gar nicht erst.
 */
function CompatCard({ itemId, itemName }: { itemId: string; itemName: string }): ReactNode {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'only' | 'never' | null>(null);
  const rules = useQuery({ queryKey: ['compat', itemId], queryFn: () => api.compat.get(itemId) });
  const alle = useQuery({ queryKey: ['items'], queryFn: () => api.items({}) });

  const speichern = useMutation({
    mutationFn: ({ m, ids }: { m: 'only' | 'never'; ids: string[] }) =>
      api.compat.set(itemId, m, ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['compat', itemId] });
      // Die Vorschläge ändern sich damit sofort.
      void qc.invalidateQueries({ queryKey: ['recommend'] });
      setMode(null);
    },
  });

  const gepflegt = rules.data?.rules ?? [];
  const nur = gepflegt.filter((r) => r.mode === 'only');
  const nie = gepflegt.filter((r) => r.mode === 'never');

  if (mode !== null) {
    const vorhanden = new Set((mode === 'only' ? nur : nie).map((r) => r.itemId));
    return (
      <ItemPicker
        titel={mode === 'only' ? `${itemName} passt nur zu …` : `${itemName} nie zusammen mit …`}
        hinweis={
          mode === 'only'
            ? 'Die Auswahl wirkt je Kategorie: wählst du fünf Hemden, kommen nur diese fünf als Oberteil in Frage. Schuhe und Hose bleiben frei.'
            : 'Diese Teile werden nie gemeinsam vorgeschlagen.'
        }
        kandidaten={(alle.data ?? []).filter((i) => i.id !== itemId)}
        vorausgewaehlt={vorhanden}
        busy={speichern.isPending}
        onAbbrechen={() => setMode(null)}
        onSpeichern={(ids) => speichern.mutate({ m: mode, ids })}
      />
    );
  }

  return (
    <Card>
      <p className="text-[15px] font-semibold">Kombinierbarkeit</p>
      <p className="mt-1 text-[13px] text-white/45">
        Was real nicht zusammenpasst — Ärmellänge, Schnitt, Farbe —, wird hier festgelegt
        und nie vorgeschlagen.
      </p>

      <p className="mt-4 text-[12px] font-medium text-white/40">Passt nur zu</p>
      {nur.length === 0 ? (
        <p className="mt-1 text-[13px] text-white/35">Keine Einschränkung — frei kombinierbar.</p>
      ) : (
        <ul className="mt-1.5 space-y-1">
          {nur.map((r) => (
            <li key={r.itemId} className="text-[14px] text-white/80">
              {r.name}
              {r.note !== null && <span className="text-white/35"> · {r.note}</span>}
            </li>
          ))}
        </ul>
      )}
      <Button variant="ghost" className="mt-2 w-full" onClick={() => setMode('only')}>
        {nur.length === 0 ? 'Passende Teile festlegen' : 'Liste ändern'}
      </Button>

      <p className="mt-5 text-[12px] font-medium text-white/40">Nie zusammen mit</p>
      {nie.length === 0 ? (
        <p className="mt-1 text-[13px] text-white/35">Nichts ausgeschlossen.</p>
      ) : (
        <ul className="mt-1.5 space-y-1">
          {nie.map((r) => (
            <li key={r.itemId} className="text-[14px] text-white/80">{r.name}</li>
          ))}
        </ul>
      )}
      <Button variant="ghost" className="mt-2 w-full" onClick={() => setMode('never')}>
        {nie.length === 0 ? 'Ausschluss festlegen' : 'Ausschluss ändern'}
      </Button>

      <ErrorBox error={rules.error ?? speichern.error} />
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <>
      <dt className="text-white/45">{label}</dt>
      <dd className="text-right text-white/85">{value}</dd>
    </>
  );
}
